import {cast, flow, getSnapshot, types} from 'mobx-state-tree';
import FileSaver from 'file-saver';
import {v4 as uuidv4} from 'uuid';
import {TProjectsMenu} from 'core/types';
import {EProjectSortType, EProjectSortField} from 'core/enums';
import {PROJECT_LIST} from 'core/mocks/projects/projects.mocks';
import {Paginator, RequestModel, ResetModel} from 'core/models';
import {PageTypeVarCustomizedProjectOut, ProjectsGetProjectsRequest} from 'api/generated';
import {ProjectOut, ProjectsApi, ProjectTypeEnum, ResponseStatusProjectOut} from 'api';

import {checkpointDbStorage, projectDbStorage} from '../../storages';

import {ProjectsFilter, ProjectsSorts} from './models';

const TypeFilters: Record<TProjectsMenu, ProjectsGetProjectsRequest> = {
  TechProcess: {deleted: false, type: ProjectTypeEnum.TechProcess},
  SamplesBlends: {deleted: false, type: ProjectTypeEnum.SamplesAndOilBlend},
  Favorites: {deleted: false, favorites: true},
  Mocked: {deleted: false, favorites: true},
  Shared: {deleted: false},
  All: {deleted: false},
  Bin: {deleted: true}
};

const MyProjectsStore = types
  .compose(
    ResetModel,
    types.model('MyProjectsStore', {
      projects: types.optional(types.array(types.frozen<ProjectOut>()), []),
      paginator: types.optional(Paginator, {size: 50, page: 1, total: 0}),
      filters: types.optional(ProjectsFilter, {}),
      sorts: types.optional(ProjectsSorts, {}),
      projectsRequest: types.optional(RequestModel, {}),
      favoriteRequest: types.optional(RequestModel, {}),
      actionRequest: types.optional(RequestModel, {}),
      exportRequest: types.optional(RequestModel, {}),
      importRequest: types.optional(RequestModel, {})
    })
  )
  .actions((self) => ({
    loadProjects: flow(function* () {
      if (self.paginator.page === 1) {
        self.projects = cast([]);
      }

      const projects: PageTypeVarCustomizedProjectOut = yield self.projectsRequest.send(
        ProjectsApi.projectsGetProjects.bind(ProjectsApi),
        {
          name: self.filters.query,
          page: self.paginator.page,
          size: self.paginator.size,
          orderBy: self.sorts.orderBy,
          orderByField: self.sorts.orderByField,
          ...TypeFilters[self.filters.activeType as TProjectsMenu]
        }
      );

      if (self.projectsRequest.isDone) {
        if (self.filters.activeType === 'Mocked') {
          self.projects = cast(PROJECT_LIST);
          self.paginator.total = PROJECT_LIST.length;
          return;
        }

        self.projects = cast([...self.projects, ...projects.items]);
        self.paginator.total = projects.total ?? projects.items.length;
      }
    }),
    createProject: flow(function* (type: ProjectTypeEnum) {
      const uuid = uuidv4();
      const response: ResponseStatusProjectOut = yield self.actionRequest.send(
        ProjectsApi.projectsCreateProject.bind(ProjectsApi),
        {
          idempotencyKey: uuid,
          projectInCreate: {type}
        }
      );

      if (response.data) {
        self.projects.push(response.data);
      }

      return response.data;
    })
  }))
  .actions((self) => ({
    renameProject: flow(function* (projectUuid: string, name: string) {
      yield self.actionRequest.send(ProjectsApi.projectsUpdateProject.bind(ProjectsApi), {
        projectUuid,
        idempotencyKey: uuidv4(),
        projectInUpdate: {name}
      });
      return self.actionRequest.isDone;
    }),
    restoreProject: flow(function* (project: ProjectOut) {
      yield self.actionRequest.send(ProjectsApi.projectsUpdateProject.bind(ProjectsApi), {
        projectUuid: project.uuid,
        idempotencyKey: uuidv4(),
        projectInUpdate: {deleted: false}
      });
      return self.actionRequest.isDone;
    }),
    deleteProject: flow(function* (project: ProjectOut) {
      if (!project.deleted) {
        yield self.actionRequest.send(ProjectsApi.projectsUpdateProject.bind(ProjectsApi), {
          projectUuid: project.uuid,
          idempotencyKey: uuidv4(),
          projectInUpdate: {deleted: true, favorite: false}
        });
      } else {
        yield self.actionRequest.send(ProjectsApi.projectsDeleteProject.bind(ProjectsApi), {
          projectUuid: project.uuid
        });

        if (self.actionRequest.isDone) {
          yield checkpointDbStorage._deleteDbs(project.checkpoints.map((c) => c.uuid));
          yield projectDbStorage.deleteLastItems(project.checkpoints.map((c) => c.uuid));
          yield projectDbStorage.deleteLastCheckpoint(project.uuid);
        }
      }

      return self.actionRequest.isDone;
    }),
    makeFavoriteProject: flow(function* (projectUuid: string, isFavorite: boolean) {
      self.projects = cast(
        self.projects.map((pr) => ({
          ...pr,
          isFavorite: pr.uuid === projectUuid ? isFavorite : pr.isFavorite
        }))
      );

      yield self.favoriteRequest.send(ProjectsApi.projectsUpdateProject.bind(ProjectsApi), {
        projectUuid: projectUuid,
        idempotencyKey: uuidv4(),
        projectInUpdate: {favorite: isFavorite}
      });
    }),
    setProjectName(projectUuid: string, name: string): void {
      self.projects = cast(
        self.projects.map((project) => ({
          ...project,
          name: project.uuid === projectUuid ? name : project.name
        }))
      );
    }
  }))
  .actions((self) => ({
    importProject: flow(function* (file: File) {
      const response: ResponseStatusProjectOut = yield self.importRequest.send(
        ProjectsApi.projectsImportProject.bind(ProjectsApi),
        {
          file: file
        }
      );

      return response?.data ?? null;
    }),
    exportProject: flow(function* (project: ProjectOut) {
      const response = yield self.exportRequest.send(
        ProjectsApi.projectsExportProject.bind(ProjectsApi),
        {
          projectUuid: project.uuid
        }
      );

      if (self.exportRequest.isDone) {
        const blob = new Blob([response], {type: 'text/plain;charset=utf-8'});
        FileSaver.saveAs(blob, `${project.name}.json`);
      }
    })
  }))
  .actions((self) => ({
    loadMore: () => {
      if (self.projects.length < self.paginator.total) {
        self.paginator.page += 1;
        self.loadProjects().then(() => {
          console.info(`[Projects]: Page ${self.paginator.page} loaded.`);
        });
      } else {
        console.info('[Projects]: No more projects to load.');
      }
    },
    setActiveType: (type: TProjectsMenu) => {
      self.filters.activeType = type;
      self.paginator.resetModel();
      self.loadProjects().then();
    },
    setActiveSort: (orderBy: EProjectSortType, orderByField: EProjectSortField) => {
      self.sorts.orderBy = orderBy;
      self.sorts.orderByField = orderByField;
      self.paginator.resetModel();
      self.loadProjects().then();
    },
    setQuery: (query: string) => {
      self.filters.query = query;
      self.paginator.resetModel();
    }
  }))
  .views((self) => ({
    get isLoading(): boolean {
      return self.projectsRequest.isPending;
    },
    get projectList(): ProjectOut[] {
      return getSnapshot(self.projects);
    },
    get isImporting(): boolean {
      return self.importRequest.isPending;
    },
    get searchText(): string {
      return self.filters.query;
    },
    get activeMenu(): TProjectsMenu {
      return self.filters.activeType as TProjectsMenu;
    }
  }));

export {MyProjectsStore};
